import React, { useEffect, useState } from "react";
import axios from "axios";
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, Check, AlertTriangle, KeyRound, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * AdminSecurity — manage 2FA for the currently logged-in admin.
 * Two states: enabled (offer disable) | disabled (offer setup wizard).
 */
export default function AdminSecurity() {
  const [status, setStatus] = useState(null); // { enabled: bool, unused_backup_codes: int }
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/2fa/status`, { withCredentials: true });
      setStatus(r.data);
    } catch { setStatus({ enabled: false, unused_backup_codes: 0 }); }
    setLoading(false);
  };

  // eslint-disable-next-line
  useEffect(() => { refresh(); }, []);

  if (loading) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#6b7280", fontFamily: "Manrope" }}>
      <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} /> Загрузка статуса 2FA…
    </div>
  );

  return (
    <div data-testid="admin-security-page" className="light-section" style={{ maxWidth: "780px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
        <Shield size={24} style={{ color: status?.enabled ? "#10B981" : "#C5A059" }} />
        <div>
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", margin: 0, fontWeight: 500, color: "#0A0A0A" }}>Двухфакторная аутентификация</h2>
          <p style={{ fontFamily: "Manrope", fontSize: "13px", color: "#6b7280", margin: "2px 0 0" }}>
            Защита от компрометации пароля. Используется Google Authenticator, Microsoft Authenticator или Authy.
          </p>
        </div>
      </div>

      {status?.enabled ? (
        <TwoFAEnabledPanel status={status} onChanged={refresh} />
      ) : (
        <TwoFASetupPanel onChanged={refresh} />
      )}
    </div>
  );
}

/* ─────────────────────── ENABLED state ─────────────────────── */
function TwoFAEnabledPanel({ status, onChanged }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const disable = async () => {
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password, code }, { withCredentials: true });
      setConfirmOpen(false); setPassword(""); setCode("");
      onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось отключить 2FA");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="twofa-enabled-panel" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", background: "#ECFDF5", border: "1px solid #D1FAE5", borderRadius: "6px", marginBottom: "16px" }}>
        <ShieldCheck size={18} style={{ color: "#10B981" }} />
        <div style={{ flex: 1, fontFamily: "Manrope", fontSize: "13px", color: "#065F46" }}>
          <b>2FA включена.</b> При каждом входе в админку вы вводите 6-значный код из приложения.
        </div>
      </div>

      <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#374151", lineHeight: 1.6, marginBottom: "16px" }}>
        Неиспользованных резервных кодов: <b data-testid="unused-backup-codes">{status.unused_backup_codes}</b> / 8.
        {status.unused_backup_codes <= 2 && (
          <div style={{ marginTop: "8px", padding: "10px 12px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "6px", color: "#92400E", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertTriangle size={14} /> Осталось мало резервных кодов. Отключите и заново включите 2FA, чтобы получить новый комплект.
          </div>
        )}
      </div>

      {!confirmOpen ? (
        <button data-testid="twofa-disable-btn" onClick={() => setConfirmOpen(true)}
          style={{ background: "white", color: "#DC2626", border: "1px solid #FECACA", borderRadius: "6px", padding: "10px 18px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <ShieldOff size={14} /> Отключить 2FA
        </button>
      ) : (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px", padding: "16px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#7F1D1D", marginBottom: "12px", fontWeight: 600 }}>
            Подтвердите паролем и 6-значным кодом (или резервным кодом):
          </div>
          <input data-testid="disable-password" type="password" placeholder="Текущий пароль" value={password} onChange={e => setPassword(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", background: "white", border: "1px solid #FCA5A5", borderRadius: "5px", fontFamily: "Manrope", fontSize: "13px", marginBottom: "8px" }} />
          <input data-testid="disable-code" type="text" placeholder="6-значный код или резервный код" value={code} onChange={e => setCode(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", background: "white", border: "1px solid #FCA5A5", borderRadius: "5px", fontFamily: "Manrope", fontSize: "13px", marginBottom: "10px", letterSpacing: "0.1em" }} />
          {error && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#DC2626", marginBottom: "8px" }}>{error}</div>}
          <div style={{ display: "flex", gap: "8px" }}>
            <button data-testid="confirm-disable-btn" onClick={disable} disabled={submitting || !password || !code}
              style={{ background: "#DC2626", color: "white", border: "none", borderRadius: "5px", padding: "9px 16px", cursor: submitting ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, opacity: (!password || !code) ? 0.5 : 1 }}>
              {submitting ? "Отключаем…" : "Подтвердить отключение"}
            </button>
            <button onClick={() => { setConfirmOpen(false); setError(""); }}
              style={{ background: "white", color: "#6B7280", border: "1px solid #D1D5DB", borderRadius: "5px", padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px" }}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── SETUP wizard ─────────────────────── */
function TwoFASetupPanel({ onChanged }) {
  const [step, setStep] = useState("password"); // password → scan → backup
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState(null); // { otpauth_uri, qr_png_base64, secret }
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copiedAll, setCopiedAll] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const startSetup = async (forceReset = false) => {
    setSubmitting(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/2fa/setup`, { password, reset: forceReset }, { withCredentials: true });
      setSetup(r.data); setStep("scan");
      setCode("");
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось начать настройку");
    } finally { setSubmitting(false); }
  };

  const regenerateQR = async () => {
    setSubmitting(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/2fa/setup`, { password, reset: true }, { withCredentials: true });
      setSetup(r.data); setCode("");
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось обновить QR");
    } finally { setSubmitting(false); }
  };

  const enable = async () => {
    setSubmitting(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/2fa/enable`, { code }, { withCredentials: true });
      setBackupCodes(r.data.backup_codes || []);
      setStep("backup");
    } catch (e) {
      setError(e.response?.data?.detail || "Неверный код. Проверьте время на устройстве.");
    } finally { setSubmitting(false); }
  };

  const copyAll = () => {
    const text = "Angiolini VIP — резервные коды 2FA\n\n" + backupCodes.map((c, i) => `${i + 1}. ${c}`).join("\n") + "\n\nКаждый код можно использовать только ОДИН раз.";
    navigator.clipboard.writeText(text);
    setCopiedAll(true); setTimeout(() => setCopiedAll(false), 2000);
  };

  const downloadCodes = () => {
    const text = "Angiolini VIP — резервные коды 2FA\nДата: " + new Date().toLocaleString("ru-RU") + "\n\n" + backupCodes.map((c, i) => `${i + 1}. ${c}`).join("\n") + "\n\nКаждый код можно использовать только ОДИН раз.\nХраните в безопасном месте — менеджере паролей или офлайн.\n";
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "angiolini-2fa-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="twofa-setup-panel" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "24px" }}>
      {step === "password" && (
        <>
          <p style={{ fontFamily: "Manrope", fontSize: "14px", color: "#374151", lineHeight: 1.65, marginTop: 0 }}>
            Чтобы включить 2FA, подтвердите ваш текущий пароль. На следующем шаге появится QR-код для сканирования в приложении-аутентификаторе.
          </p>
          <input data-testid="setup-password" type="password" placeholder="Текущий пароль" value={password} onChange={e => setPassword(e.target.value)}
            style={{ width: "100%", padding: "11px 14px", background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: "5px", fontFamily: "Manrope", fontSize: "14px", marginBottom: "12px" }} />
          {error && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#DC2626", marginBottom: "10px" }}>{error}</div>}
          <button data-testid="twofa-start-btn" onClick={() => startSetup(false)} disabled={submitting || !password}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "5px", padding: "11px 22px", cursor: submitting ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "8px", opacity: !password ? 0.5 : 1 }}>
            {submitting ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Подождите…</> : <><Shield size={14} /> Включить 2FA</>}
          </button>
        </>
      )}

      {step === "scan" && setup && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "24px", alignItems: "start" }} className="twofa-scan-grid">
            <div style={{ background: "#fafafa", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
              <img data-testid="twofa-qr" src={`data:image/png;base64,${setup.qr_png_base64}`} alt="QR" style={{ width: "200px", height: "200px", display: "block" }} />
            </div>
            <div>
              <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", margin: "0 0 12px", color: "#0A0A0A" }}>1. Отсканируйте QR-код</h3>
              <p style={{ fontFamily: "Manrope", fontSize: "13px", color: "#6b7280", lineHeight: 1.6, marginTop: 0, marginBottom: "16px" }}>
                Откройте <b>Google Authenticator</b>, <b>Microsoft Authenticator</b> или <b>Authy</b> на телефоне → «Добавить аккаунт» → «Сканировать QR».
              </p>
              <div style={{ background: "#F9FAFB", padding: "10px 12px", borderRadius: "6px", fontFamily: "Manrope", fontSize: "11px", color: "#6B7280", marginBottom: "10px" }}>
                Если QR не сканируется, введите ключ вручную:
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                  <code data-testid="twofa-secret" style={{ flex: 1, fontFamily: "monospace", fontSize: "12px", color: "#0A0A0A", padding: "5px 8px", background: "white", borderRadius: "4px", border: "1px solid #E5E7EB", letterSpacing: "0.05em", overflowWrap: "anywhere" }}>{setup.secret}</code>
                  <button onClick={() => { navigator.clipboard.writeText(setup.secret); setSecretCopied(true); setTimeout(() => setSecretCopied(false), 1500); }}
                    style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "4px", padding: "5px 8px", cursor: "pointer", color: "#6B7280" }}>
                    {secretCopied ? <Check size={13} /> : <Copy size={13} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "20px", marginTop: "20px" }}>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", margin: "0 0 8px", color: "#0A0A0A" }}>2. Введите первый код из приложения</h3>
            <p style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280", margin: "0 0 12px" }}>
              Код меняется каждые 30 секунд. Введите тот, который виден сейчас.
            </p>
            <input data-testid="twofa-first-code" type="text" placeholder="6-значный код" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6}
              style={{ width: "180px", padding: "12px 14px", background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: "5px", fontFamily: "monospace", fontSize: "18px", letterSpacing: "0.3em", textAlign: "center", marginRight: "10px" }} />
            <button data-testid="twofa-enable-btn" onClick={enable} disabled={submitting || code.length !== 6}
              style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "5px", padding: "11px 22px", cursor: submitting ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: code.length === 6 ? 1 : 0.5 }}>
              {submitting ? "Проверка…" : "Подтвердить"}
            </button>
            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "6px", padding: "12px", marginTop: "12px" }}>
                <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#DC2626", fontWeight: 600, marginBottom: "6px" }}>{error}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#7F1D1D", lineHeight: 1.5, marginBottom: "8px" }}>
                  Возможные причины:<br />
                  • В приложении остался старый QR-код (сканировался ранее) — нажмите ниже «Сгенерировать новый QR»<br />
                  • Время на телефоне неточное — в настройках Android/iOS включите «Установить автоматически»<br />
                  • Просто подождите 30 секунд и введите следующий код
                </div>
                <button data-testid="twofa-regen-btn" type="button" onClick={regenerateQR} disabled={submitting}
                  style={{ background: "white", color: "#DC2626", border: "1px solid #FCA5A5", borderRadius: "4px", padding: "7px 14px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  ↻ Сгенерировать новый QR
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {step === "backup" && (
        <div data-testid="twofa-backup-step">
          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", background: "#ECFDF5", border: "1px solid #D1FAE5", borderRadius: "6px", marginBottom: "20px" }}>
            <ShieldCheck size={18} style={{ color: "#10B981" }} />
            <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#065F46" }}>
              <b>2FA успешно включена.</b> С этого момента при входе в админку требуется код из приложения.
            </div>
          </div>

          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", margin: "0 0 6px", color: "#0A0A0A", display: "flex", alignItems: "center", gap: "10px" }}>
            <KeyRound size={20} style={{ color: "#C5A059" }} /> Сохраните резервные коды
          </h3>
          <p style={{ fontFamily: "Manrope", fontSize: "13px", color: "#7F1D1D", lineHeight: 1.6, margin: "0 0 16px", padding: "10px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "6px" }}>
            <AlertTriangle size={14} style={{ display: "inline", verticalAlign: "-2px", marginRight: "6px" }} />
            Эти 8 кодов показываются <b>один раз</b>. Сохраните их в менеджер паролей или распечатайте. Каждый код работает <b>только один раз</b> — для входа, если потеряете доступ к телефону.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
            {backupCodes.map((c, i) => (
              <div key={c} data-testid={`backup-code-${i}`} style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", padding: "10px 14px", borderRadius: "6px", fontFamily: "monospace", fontSize: "15px", color: "#0A0A0A", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ color: "#9CA3AF", fontFamily: "Manrope", fontSize: "11px", minWidth: "16px" }}>{i + 1}.</span>
                <b>{c}</b>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button data-testid="copy-codes-btn" onClick={copyAll}
              style={{ background: "white", border: "1px solid #D1D5DB", borderRadius: "5px", padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, color: "#374151", display: "inline-flex", alignItems: "center", gap: "8px" }}>
              {copiedAll ? <><Check size={14} /> Скопировано</> : <><Copy size={14} /> Копировать все</>}
            </button>
            <button data-testid="download-codes-btn" onClick={downloadCodes}
              style={{ background: "white", border: "1px solid #D1D5DB", borderRadius: "5px", padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, color: "#374151", display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <Download size={14} /> Скачать .txt
            </button>
            <button data-testid="finish-setup-btn" onClick={onChanged}
              style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "5px", padding: "9px 22px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Готово
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
